<template>
  <section class="dialer-call">
    <div v-if="joinError" class="dialer-call__error" role="alert">
      <p>{{ joinError }}</p>
    </div>
    <div class="dialer-call__main">
      <main
        class="video-screen"
        :class="{
          'video-screen--swapped': isLocalMaximized && !showWaitingForRemote,
          'video-screen--waiting': showWaitingForRemote,
        }"
      >
        <!-- Remoto: principal por padrão / PiP quando trocado -->
        <div
          id="expand_stream"
          class="video-stage"
          :class="
            isLocalMaximized && !showWaitingForRemote
              ? 'video-stage--pip'
              : 'video-stage--main'
          "
        ></div>


        <div
          v-if="showWaitingForRemote"
          class="dialer-call__waiting"
          role="status"
          aria-live="polite"
        >
          <p class="dialer-call__waiting-message">
            {{ waitingRemoteMessage }}
          </p>
          <p v-if="!secondaryHasBeenPresent" class="dialer-call__waiting-time" aria-live="polite">
            Tempo de espera: {{ waitingTimeLabel }}
          </p>
          <ConnectionLevel
            class="dialer-call__connection dialer-call__connection--waiting"
            :objConnection="objConnection"
            :objAgora="objAgora"
            :inChannel="inChannel"
          />
          <div class="dialer-call__waiting-spinner" aria-hidden="true"></div>
        </div>

        <div v-if="!showWaitingForRemote" class="dialer-call__hud">
          <ConnectionLevel
            class="dialer-call__connection dialer-call__connection--call"
            :objConnection="objConnection"
            :objAgora="objAgora"
            :inChannel="inChannel"
          />
          <p
            v-if="showCallTime"
            class="dialer-call__call-time"
            aria-live="polite"
          >
            Tempo de chamada: {{ callTimeLabel }}
          </p>
        </div>

        <!-- Local: PiP (também na espera) / principal quando trocado -->
        <div
          class="video-stage"
          :class="
            isLocalMaximized && !showWaitingForRemote
              ? 'video-stage--main'
              : 'video-stage--pip'
          "
          :aria-label="primaryName || 'Você'"
          @click.stop="onLocalStageClick"
        >
          <div class="local_stream" id="local_stream"></div>
        </div>

        <button
          v-if="!showWaitingForRemote"
          type="button"
          class="video-stage__swap"
          aria-label="Maximizar o vídeo menor"
          @click.stop="onPipSwapClick"
        ></button>

        <!-- Chat overlay (aberto pelo botão) -->
        <aside v-if="chatAvailable" v-show="showChatMessages" class="dialer-call__chat">
          <Chat
            ref="chat"
            :primaryUID="primaryUID"
            :secondaryUID="secondaryUID"
            :roomName="objAgora.nome"
            :showMessages="true"
            :options="options"
            :primaryName="primaryName"
            :secondaryName="secondaryName"
            @onNewMessage="onReceiveNewMessage"
            @close-chat="closeChat"
          />
        </aside>

        <!-- Controles sempre visíveis (incluindo na espera) -->
        <div class="video-screen__center-controls">
          <IconButton
            v-if="chatAvailable"
            :key="showChatMessages ? 'chat-on' : 'chat-off'"
            icon="chat"
            :count="messageCount"
            title="Abrir/Fechar Chat"
            :initialActive="showChatMessages"
            @toggle="openChat"
          />

          <div ref="cameraControl" class="dialer-call__camera-control">
            <IconButton
              icon="camera"
              title="Opções da Câmera"
              :initialActive="cameraActive"
              :toggleOnClick="false"
              @toggle="toggleCameraMenu"
            />

            <div
              v-if="showCameraMenu"
              class="dialer-call__camera-menu"
              role="menu"
              @keydown.esc="closeCameraMenu"
            >
              <button
                type="button"
                class="dialer-call__camera-menu-item"
                role="menuitem"
                @click="onCameraMenuToggleCamera"
              >
                {{ cameraActive ? 'Desligar câmera' : 'Ligar câmera' }}
              </button>
              <button
                type="button"
                class="dialer-call__camera-menu-item"
                role="menuitem"
                @click="onCameraMenuToggleBlur"
              >
                {{ blurActive ? 'Desativar desfoque' : 'Ativar desfoque' }}
              </button>
            </div>
          </div>

          <IconButton
            icon="microphone"
            title="Ativar/Desativar Microfone"
            @toggle="toggleMicrophone"
          />

          <IconButton
            v-if="isMobileApp && canSwitchCamera"
            icon="camera-switch"
            title="Inverter Câmera"
            @toggle="switchCamera"
          />

          <IconButton
            color="red"
            icon="call"
            title="Sair da Chamada"
            @toggle="requestLeaveCall"
          />
        </div>

        <div
          v-if="showLeaveConfirm"
          ref="leaveConfirm"
          class="dialer-call__leave-confirm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="leave-confirm-title"
          tabindex="-1"
          @click.self="cancelLeaveCall"
          @keydown.esc="cancelLeaveCall"
        >
          <div class="dialer-call__leave-confirm-card">
            <p id="leave-confirm-title" class="dialer-call__leave-confirm-title">Sair da chamada?</p>
            <div class="dialer-call__leave-confirm-actions">
              <button
                type="button"
                class="dialer-call__leave-confirm-cancel"
                @click="cancelLeaveCall"
              >
                Cancelar
              </button>
              <button
                type="button"
                class="dialer-call__leave-confirm-leave"
                @click="confirmLeaveCall"
              >
                Sair
              </button>
            </div>
          </div>
        </div>

        <div
          v-if="showEndingCall"
          class="dialer-call__ending"
          role="status"
          aria-live="polite"
        >
          <p class="dialer-call__ending-message">Encerrando atendimento...</p>
          <div class="dialer-call__waiting-spinner" aria-hidden="true"></div>
        </div>
      </main>
    </div>
  </section>
</template>

<script>
import "./DialerCall.css";

import VideoCallService, { EVENTS } from "@/agora-service/VideoCallService.js";
import websocketService from "@/services/websocket-service.js";
import { normalizeDialerUid } from "@/agora-service/AgoraUid.js";
import {
  insertRoom,
  getClienteId,
  getToken,
  insertNetworkQuality,
  updateStatusByUID,
  startRoomWaiting,
  updateRoomTimes,
  parseRoomTimes,
  getStatusRoomByUIDs,
  getDialerSessionId,
  refreshRoomSession,
  releaseRoomSession,
  releaseRoomSessionOnUnload,
  isUidAlreadyInRoomError,
  UID_ALREADY_IN_ROOM,
} from "@/services/service-api.js";

const ROOM_SESSION_HEARTBEAT_MS = 5000;
const REMOTE_QUIT_GRACE_MS = 3000;

import IconButton from "@/components/icon-button/IconButton.vue";
import ConnectionLevel from "@/components/connection-level/ConnectionLevel.vue";
import Chat from "@/components/chat/Chat.vue";

export default {
  name: "DialerCall",
  components: {
    IconButton,
    ConnectionLevel,
    Chat,
  },

  props: {
    system: {
      type: String,
      required: false,
      default: "SALA",
    },
    primaryUID: {
      type: [Number, String],
      required: true,
    },
    secondaryUID: {
      type: [Number, String],
      required: true,
    },
    options: {
      type: Object,
      default: null,
    },
    awaitSecondary: {
      type: Boolean,
      default: false,
    },
    primaryName: {
      type: String,
      required: false,
      default: '',
    },
    secondaryName: {
      type: String,
      required: false,
      default: '',
    },
  },

  data() {
    return {
      joinError: '',
      blockedByOtherDevice: false,
      sessionId: getDialerSessionId(),
      hasRoomSession: false,
      sessionHeartbeatTimer: null,
      inChannel: false,
      cameraActive: true,
      isMobileApp: typeof window !== 'undefined' && Boolean(window.ReactNativeWebView),
      canSwitchCamera: false,
      isSwitchingCamera: false,
      cameraFacing: 'user',
      microphoneActive: true,
      blurActive: false,
      showCameraMenu: false,
      showChatMessages: false,
      showLeaveConfirm: false,
      isLeavingCall: false,
      isLocalMaximized: false,
      isSwapLocked: false,
      remotePresent: false,
      remoteAbsenceConfirmed: false,
      remoteQuitPending: false,
      remoteQuitGraceTimer: null,
      waitingPollTimer: null,
      presenceWatchTimer: null,
      waitStartedAt: null,
      callStartedAt: null,
      callPausedAt: null,
      callPausedMs: 0,
      accumulatedWaitMs: 0,
      accumulatedCallMs: 0,
      secondaryHasBeenPresent: false,
      effectiveCallStartedAt: null,
      effectiveCallEndedAt: null,
      waitClockTimer: null,
      waitNow: Date.now(),
      messageCount: 0,

      // ============================================
      // AGORA SDK CONFIGURATION
      // ============================================
      objAgora: {
        /** UID único do usuário no canal Agora (Number) */
        primaryUID: null,
        secondaryUID: null,
        /** Token de autenticação do Agora (String) */
        token: null,
      },
      objConnection: {
        /** Nível de conexão atual (Number) */
        downlinkNetworkQuality: null,
        uplinkNetworkQuality: null,
      },
      // ============================================
      // ESTADO DA MÍDIA
      // ============================================
      videoOn: true,
      audioOn: true,

      // ============================================
      // CONTROLE DE SALA
      // ============================================
      enterTokenRoom: false,
      professionalRemoteStreamId: null,

      // ============================================
      // CONTROLE DE QUALIDADE DE CONEXÃO
      // ============================================
      lastNetworkQualityInsert: 0,
      networkQualityThrottleTime: 2000, // 2 segundos em milissegundos

      // ============================================
      // CONTROLE DE PARTICIPANTES
      // ============================================
      activeParticipants: 1, // Contador de participantes ativos (inicia com o usuário local)
      maxParticipants: 3, // Limite máximo de participantes
    };
  },

  computed: {
    showWaitingForRemote() {
      return (
        this.awaitSecondary &&
        !this.remotePresent &&
        !this.joinError &&
        !this.isLeavingCall &&
        !this.remoteQuitPending
      );
    },
    showEndingCall() {
      return (this.isLeavingCall || this.remoteQuitPending) && !this.blockedByOtherDevice;
    },
    waitingRemoteMessage() {
      const name = String(this.secondaryName || '').trim();
      if (name) {
        return `Esperando ${name} para o atendimento.`;
      }
      return 'Esperando o profissional para o atendimento.';
    },
    chatAvailable() {
      return Boolean(this.objAgora.nome) && !this.blockedByOtherDevice;
    },
    showCallTime() {
      return !this.showWaitingForRemote && this.remotePresent && this.callStartedAt != null;
    },
    waitingTimeLabel() {
      return this.formatElapsedLabelFromMs(this.getTotalWaitMs());
    },
    callTimeLabel() {
      return this.formatElapsedLabelFromMs(this.getTotalCallMs());
    },
  },

  created() {
    if (this.awaitSecondary) {
      this.waitStartedAt = Date.now();
      this.startElapsedClock();
    }
  },

  mounted() {
    window.addEventListener('pagehide', this.onPageHide);
    // Iniciar sala automaticamente
    this.actStartRoom();
  },

  beforeDestroy() {
    document.removeEventListener('click', this.onDocumentClickForCameraMenu, true);
    VideoCallService.onCameraChanged(null);
    this.stopWaitingPoll();
    this.stopPresenceWatch();
    this.stopElapsedClock();
    this.stopSessionHeartbeat();
    this.stopRemoteQuitGrace();
    if (this.hasRoomSession) {
      this.hasRoomSession = false;
      releaseRoomSessionOnUnload(this.objAgora.id, this.objAgora.primaryUID, this.sessionId);
    }
    VideoCallService.cleanEvents();

    if (typeof this.remoteLeftRemoveListener === 'function') {
      this.remoteLeftRemoveListener()
    }    
  },

  methods: {
    buildTimesPayload(chamadaFinalizada = false) {
      return {
        tempoEsperaMs: this.accumulatedWaitMs,
        duracaoChamadaMs: this.accumulatedCallMs,
        atendimentoIniciadoEm: this.effectiveCallStartedAt
          ? new Date(this.effectiveCallStartedAt).toISOString()
          : null,
        atendimentoFinalizadoEm: this.effectiveCallEndedAt
          ? new Date(this.effectiveCallEndedAt).toISOString()
          : null,
        secundarioJaPresente: this.secondaryHasBeenPresent,
        chamadaFinalizada,
      };
    },

    applyRoomTimesFromResponse(sala) {
      const parsed = parseRoomTimes(sala);
      if (!parsed || parsed.chamadaFinalizada || this.remotePresent) return;

      this.accumulatedWaitMs = parsed.tempoEsperaMs;
      this.accumulatedCallMs = parsed.duracaoChamadaMs;
      this.secondaryHasBeenPresent = this.secondaryHasBeenPresent || parsed.secundarioJaPresente;

      if (parsed.duracaoChamadaMs <= 0) return;

      if (parsed.atendimentoIniciadoEm) {
        const startedAt = new Date(parsed.atendimentoIniciadoEm).getTime();
        this.effectiveCallStartedAt = Number.isFinite(startedAt) ? startedAt : this.effectiveCallStartedAt;
      }

      if (parsed.atendimentoFinalizadoEm) {
        const endedAt = new Date(parsed.atendimentoFinalizadoEm).getTime();
        this.effectiveCallEndedAt = Number.isFinite(endedAt) ? endedAt : this.effectiveCallEndedAt;
      }
    },

    resumeLiveCallFromRoom(sala) {
      if (!this.awaitSecondary || !sala?.atendimentoIniciadoEm) return;

      const startedAt = new Date(sala.atendimentoIniciadoEm).getTime();
      if (!Number.isFinite(startedAt)) return;

      this.secondaryHasBeenPresent = true;
      this.effectiveCallStartedAt = this.effectiveCallStartedAt || startedAt;
      this.waitStartedAt = null;
    },

    buildEventTimesPayload(chamadaFinalizada = false) {
      return {
        roomId: this.objAgora?.id ?? null,
        ...this.buildTimesPayload(chamadaFinalizada),
        waitTimeMs: this.accumulatedWaitMs,
        callDurationMs: this.accumulatedCallMs,
        callStartedAt: this.effectiveCallStartedAt,
        callEndedAt: this.effectiveCallEndedAt || Date.now(),
      };
    },

    formatElapsedLabel(startedAt) {
      if (!startedAt) return "00:00:00";
      return this.formatElapsedLabelFromMs(Math.max(0, this.waitNow - startedAt));
    },

    formatElapsedLabelFromMs(ms) {
      const totalSeconds = Math.floor(Math.max(0, ms) / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      return [String(hours).padStart(2, "0"), String(minutes).padStart(2, "0"), String(seconds).padStart(2, "0"),].join(":");
    },

    getActiveWaitMs() {
      if (!this.waitStartedAt) return 0;
      return Math.max(0, this.waitNow - this.waitStartedAt);
    },

    getActiveCallMs() {
      if (!this.callStartedAt) return 0;
      const pausedMs = this.callPausedMs + (this.callPausedAt ? this.waitNow - this.callPausedAt : 0);
      return Math.max(0, this.waitNow - this.callStartedAt - pausedMs);
    },

    getTotalWaitMs() {
      return this.accumulatedWaitMs + this.getActiveWaitMs();
    },

    getTotalCallMs() {
      const activeCallMs = this.remotePresent && this.callStartedAt ? this.getActiveCallMs() : 0;
      return this.accumulatedCallMs + activeCallMs;
    },

    flushCurrentWaitSegment() {
      if (!this.waitStartedAt) return;
      this.accumulatedWaitMs += Math.max(0, Date.now() - this.waitStartedAt);
      this.waitStartedAt = null;
    },

    flushCurrentCallSegment() {
      if (!this.callStartedAt) return;
      this.waitNow = Date.now();
      this.accumulatedCallMs += this.getActiveCallMs();
      this.effectiveCallEndedAt = Date.now();
      this.callStartedAt = null;
      this.callPausedAt = null;
      this.callPausedMs = 0;
    },

    async persistAccumulatedTimes() {
      if (!this.awaitSecondary || this.objAgora?.id == null) return;

      try {
        const sala = await updateRoomTimes(this.objAgora.id, this.buildTimesPayload(false));
        this.applyRoomTimesFromResponse(sala);
      } catch {}
    },

    async clearPersistedTimes() {
      if (this.objAgora?.id == null) return;

      try {
        await updateRoomTimes(this.objAgora.id, this.buildTimesPayload(true));
      } catch {}
    },

    markRemotePresent() {
      if (this.remotePresent || this.isLeavingCall) return;
      this.remotePresent = true;
      this.remoteAbsenceConfirmed = false;
      this.stopRemoteQuitGrace();
      this.isLocalMaximized = false;
      this.stopWaitingPoll();
      this.flushCurrentWaitSegment();
      this.secondaryHasBeenPresent = true;
      if (!this.effectiveCallStartedAt) {
        this.effectiveCallStartedAt = Date.now();
      }
      if (this.callPausedAt) {
        this.callPausedMs += Date.now() - this.callPausedAt;
        this.callPausedAt = null;
      }
      if (!this.callStartedAt) {
        this.callStartedAt = Date.now();
      }
      this.startElapsedClock();
      this.startPresenceWatch();
      VideoCallService.ensureRemotePlayback().catch((_error) => {});
    },

    markRemoteAbsent() {
      if (this.isLeavingCall) return;
      if (!this.remotePresent && this.waitingPollTimer != null) return;

      this.remotePresent = false;
      this.isLocalMaximized = false;
      this.showChatMessages = false;
      VideoCallService.clearRemotePlayback();
      this.flushCurrentCallSegment();
      if (this.awaitSecondary) {
        this.startWaitingPoll();
        this.startPresenceWatch();
      }
    },

    /**
     * Saída voluntária do outro participante: segura a tela de espera até o
     * evento "left" do websocket encerrar esta chamada (e abrir o resumo).
     */
    startRemoteQuitGrace() {
      this.stopRemoteQuitGrace();
      this.remoteQuitPending = true;
      this.remoteQuitGraceTimer = window.setTimeout(() => {
        this.remoteQuitGraceTimer = null;
        this.remoteQuitPending = false;
      }, REMOTE_QUIT_GRACE_MS);
    },

    stopRemoteQuitGrace() {
      if (this.remoteQuitGraceTimer != null) {
        window.clearTimeout(this.remoteQuitGraceTimer);
        this.remoteQuitGraceTimer = null;
      }
      this.remoteQuitPending = false;
    },

    startPresenceWatch() {
      if (!this.awaitSecondary) return;
      this.stopPresenceWatch();
      this.presenceWatchTimer = window.setInterval(() => {
        void this.syncRemotePresence();
      }, 1500);
    },

    stopPresenceWatch() {
      if (this.presenceWatchTimer != null) {
        window.clearInterval(this.presenceWatchTimer);
        this.presenceWatchTimer = null;
      }
    },

    syncRemotePresence() {
      if (!this.awaitSecondary) return;

      const hasRemote = VideoCallService.hasRemoteUser();
      if (hasRemote && !this.remotePresent) {
        void this.checkRemotePresence();
        return;
      }
      if (!hasRemote && this.remotePresent) {
        this.markRemoteAbsent();
        return;
      }
      if (this.remotePresent) {
        void this.verifyRemotePresenceViaApi();
      }
    },

    async verifyRemotePresenceViaApi() {
      if (!this.awaitSecondary || !this.remotePresent) return;
      if (!this.objAgora?.primaryUID || !this.objAgora?.secondaryUID) return;

      if (await this.isRemoteInRoomViaApi()) return;
      if (!this.remotePresent) return;

      this.remoteAbsenceConfirmed = true;
      VideoCallService.clearRemotePlayback();
      this.markRemoteAbsent();
    },

    async isRemoteInRoomViaApi() {
      try {
        const status = await getStatusRoomByUIDs(this.objAgora.primaryUID, this.objAgora.secondaryUID);
        return this.isOtherParticipantInRoom(status);
      } catch {
        return true;
      }
    },

    startWaitingPoll() {
      if (!this.awaitSecondary || this.remotePresent) return;
      this.stopWaitingPoll();
      this.waitingPollTimer = window.setInterval(() => {
        this.checkRemotePresence();
      }, 1500);
      this.checkRemotePresence();
    },

    stopWaitingPoll() {
      if (this.waitingPollTimer != null) {
        window.clearInterval(this.waitingPollTimer);
        this.waitingPollTimer = null;
      }
    },

    async checkRemotePresence() {
      if (!this.awaitSecondary || this.remotePresent || this.isLeavingCall) return;

      await VideoCallService.ensureRemotePlayback().catch(() => {});

      if (!VideoCallService.hasRemoteUser()) return;
      if (this.remoteAbsenceConfirmed && !(await this.isRemoteInRoomViaApi())) return;

      this.markRemotePresent();
    },

    startElapsedClock() {
      this.stopElapsedClock();
      this.waitNow = Date.now();
      this.waitClockTimer = window.setInterval(() => {
        this.waitNow = Date.now();
      }, 1000);
    },

    stopElapsedClock() {
      if (this.waitClockTimer != null) {
        window.clearInterval(this.waitClockTimer);
        this.waitClockTimer = null;
      }
    },

    applyWaitingTimestamp(value) {
      const parsed = value ? new Date(value).getTime() : NaN;
      this.waitStartedAt = Number.isFinite(parsed) ? parsed : Date.now();
      this.waitNow = Date.now();
      this.startElapsedClock();
    },

    async persistWaitingStart() {
      if (!this.awaitSecondary) return;

      if (this.objAgora.id == null) {
        this.applyWaitingTimestamp(this.waitStartedAt || Date.now());
        return;
      }

      try {
        const sala = await startRoomWaiting(this.objAgora.id);
        this.applyRoomTimesFromResponse(sala);
        if (this.remotePresent || this.secondaryHasBeenPresent) return;

        const resumingWait = this.accumulatedWaitMs > 0;
        this.applyWaitingTimestamp(resumingWait ? this.waitStartedAt || Date.now() : sala?.esperaIniciadaEm || this.waitStartedAt || Date.now());
      } catch (_error) {
        if (this.remotePresent || this.secondaryHasBeenPresent) return;
        this.applyWaitingTimestamp(this.waitStartedAt || Date.now());
      }
    },

    isOtherParticipantInRoom(status) {
      if (!status?.exists) return false;
      const local = normalizeDialerUid(this.primaryUID);
      const primario = normalizeDialerUid(status.primarioNaSala ?? "0");
      const secundario = normalizeDialerUid(status.secundarioNaSala ?? "0");
      const empty = "00000000000";
      const otherInPrimario = primario !== empty && primario !== local;
      const otherInSecundario = secundario !== empty && secundario !== local;
      return otherInPrimario || otherInSecundario;
    },

    async _bootLocalPreview() {
      for (let attempt = 0; attempt < 10; attempt += 1) {
        await this.$nextTick();
        const started = await VideoCallService.startLocalPreview();
        if (started) return true;
        await new Promise((resolve) => window.setTimeout(resolve, 120));
      }
      return false;
    },

    async actStartRoom() {
      const hasPermissions = await this._getPermissions();
      if (!hasPermissions) {
        return;
      }

      this.objAgora.primaryUID = normalizeDialerUid(this.primaryUID);
      this.objAgora.secondaryUID = normalizeDialerUid(this.secondaryUID);


      let roomRecord;
      try {
        roomRecord = await this._insertRoomRecord();
      } catch (error) {
        if (isUidAlreadyInRoomError(error)) {
          this.showBlockedByOtherDevice();
          return;
        }
      }
      if (!roomRecord?.token || !roomRecord?.nome) {
        this.joinError = 'Não foi possível entrar na sala. Tente novamente.';
        this.$emit("left-call");
        return;
      }

      const callerPrimary = this.objAgora.primaryUID;
      const callerSecondary = this.objAgora.secondaryUID;
      this.objAgora = {
        ...this.objAgora,
        ...roomRecord,
        primaryUID: callerPrimary,
        secondaryUID: callerSecondary,
        token: roomRecord.token,
        nome: roomRecord.nome,
        id: roomRecord.id,
      };
      this.hasRoomSession = roomRecord.id != null;
      this.startSessionHeartbeat();

      this.applyRoomTimesFromResponse(roomRecord);
      this.resumeLiveCallFromRoom(roomRecord);

      this._setupAgoraListeners();
      try {
        await this._joinAgoraChannel();
        this.inChannel = VideoCallService.getConnectionState() === 'CONNECTED';
        await VideoCallService.ensureLocalPreview();
        if (!VideoCallService.hasLocalVideoTrack()) {
          this.joinError = 'Não foi possível acessar a câmera. Verifique as permissões do app.';
          await this.releaseCurrentRoomSession();
          this.$emit('left-call');
          return;
        }
        if (this.isMobileApp) {
          VideoCallService.onCameraChanged(() => this.refreshCameraAvailability());
          void this.refreshCameraAvailability();
        }
        if (this.objAgora.id != null) {
          const sala = await updateStatusByUID(this.objAgora.id, this.objAgora.primaryUID);
          this.applyRoomTimesFromResponse(sala);
        }
        await this.persistWaitingStart();
        this.$emit("emit-event", {
          eventName: "enterRoom",
          data: { status: true },
        });
        await VideoCallService.ensureRemotePlayback();
      } catch {
        this.joinError = 'Não foi possível entrar na chamada de vídeo. Tente novamente.';
        await this.releaseCurrentRoomSession();
        this.$emit('left-call');
        return;
      }

      this.startWaitingPoll();
      this.startPresenceWatch();
      this.startRemotePlaybackRetry();
      this.$forceUpdate();
      this.onLeftCall();
    },

    showBlockedByOtherDevice() {
      this.$refs.chat?.detach();
      if (typeof this.remoteLeftRemoveListener === 'function') {
        this.remoteLeftRemoveListener();
        this.remoteLeftRemoveListener = null;
      }
      this.showChatMessages = false;
      this.showLeaveConfirm = false;
      this.closeCameraMenu();
      this.blockedByOtherDevice = true;
      this.hasRoomSession = false;
      this.stopSessionHeartbeat();
      this.stopWaitingPoll();
      this.stopPresenceWatch();
      this.stopElapsedClock();
      this.joinError = 'Já existe uma sessão ativa em outro dispositivo.\nPara entrar na sala, encerre a sessão no outro dispositivo e tente novamente.';
      this.$emit("emit-event", {
        eventName: "alreadyInRoom",
        data: { status: false, code: UID_ALREADY_IN_ROOM },
      });
    },

    /**
     * Outro dispositivo assumiu a vaga (ex.: este ficou suspenso além do TTL e o Agora o derrubou).
     */
    async handleSessionTakenOver() {
      if (this.blockedByOtherDevice) return;
      this.showBlockedByOtherDevice();
      VideoCallService.cleanEvents();
      try {
        await VideoCallService.leaveRoom();
      } catch {}
    },

    startSessionHeartbeat() {
      this.stopSessionHeartbeat();
      if (!this.hasRoomSession) return;
      this.sessionHeartbeatTimer = window.setInterval(() => {
        void this.checkRoomSession();
      }, ROOM_SESSION_HEARTBEAT_MS);
    },

    stopSessionHeartbeat() {
      if (this.sessionHeartbeatTimer != null) {
        window.clearInterval(this.sessionHeartbeatTimer);
        this.sessionHeartbeatTimer = null;
      }
    },

    async checkRoomSession() {
      if (!this.hasRoomSession || this.objAgora?.id == null) return;
      try {
        await refreshRoomSession(this.objAgora.id, this.objAgora.primaryUID, this.sessionId);
      } catch (error) {
        if (isUidAlreadyInRoomError(error)) {
          await this.handleSessionTakenOver();
        }
      }
    },

    async releaseCurrentRoomSession() {
      this.stopSessionHeartbeat();
      if (!this.hasRoomSession || this.objAgora?.id == null) return;
      this.hasRoomSession = false;
      try {
        await releaseRoomSession(this.objAgora.id, this.objAgora.primaryUID, this.sessionId);
      } catch {}
    },

    onPageHide() {
      if (!this.hasRoomSession || this.objAgora?.id == null) return;
      this.stopSessionHeartbeat();
      this.hasRoomSession = false;
      releaseRoomSessionOnUnload(this.objAgora.id, this.objAgora.primaryUID, this.sessionId);
    },

    onLeftCall() {
      if (typeof this.remoteLeftRemoveListener === 'function') {
        this.remoteLeftRemoveListener()
      }
      
      this.remoteLeftRemoveListener = websocketService.onLeftCall((message) => {
        if (message?.remoteUid && message.remoteUid === this.primaryUID) {
          this.confirmLeaveCall()
        }
      })      
    },

    startRemotePlaybackRetry() {
      let attempts = 0;
      const timer = window.setInterval(() => {
        attempts += 1;
        VideoCallService.ensureRemotePlayback()
          .then(() => {
            if (VideoCallService.hasRemoteUser()) {
              this.markRemotePresent();
            }
          })
          .catch(() => {});
        if (attempts >= 20) {
          window.clearInterval(timer);
        }
      }, 500);
    },

    async _getPermissions() {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          return false;
        }
        return true;
      } catch {
        this.joinError = 'Não foi possível acessar câmera/microfone.';
        return false;
      }
    },

    /**
     * Conecta ao canal do Agora SDK e inicia stream de vídeo/áudio
     * @async
     * @returns {Promise<void>}
     */
    async _joinAgoraChannel() {
      await VideoCallService.joinChannel({
        uid: this.objAgora.primaryUID,
        channel: this.objAgora.nome,
        token: this.objAgora.token,
      });
    },

    /**
     * Configura listeners de eventos do Agora SDK
     * Eventos principais:
     * - user-joined: Outro usuário entrou no canal
     * - user-published: Outro usuário publicou stream de vídeo/áudio
     * - user-unpublished: Outro usuário parou stream
     * - user-left: Outro usuário saiu do canal
     */
    _setupAgoraListeners() {
      VideoCallService.cleanEvents();
      VideoCallService.on(EVENTS.USER_JOINED, async (input) => {
        await VideoCallService.ensureRemotePlayback().catch(() => {});
        this.markRemotePresent();
        this.$emit("emit-event", {
          eventName: "enterSecondaryUID",
          data: { status: true, uid: input.user.uid },
        });

        // Incrementa contador de participantes
        this.activeParticipants++;

        // Verifica se excedeu o limite após incrementar
        if (this.activeParticipants > this.maxParticipants) {
          alert(`Sala cheia! Limite máximo de ${this.maxParticipants} participantes atingido.`);

          // Decrementa o contador e sai da sala
          this.activeParticipants--;
          this.$emit("emit-event", {
            eventName: "enterSecondaryUID",
            data: { status: false, uid: input.user.uid },
          });
          return;
        }
      });
      VideoCallService.on(EVENTS.USER_PUBLISHED, async () => {
        await VideoCallService.ensureRemotePlayback().catch(() => {});
        this.markRemotePresent();
      });
      VideoCallService.on(EVENTS.USER_UNPUBLISHED, () => {
        this.syncRemotePresence();
      });
      VideoCallService.on(EVENTS.USER_LEFT, (input) => {
        if (this.activeParticipants > 1) {
          this.activeParticipants--;
        }

        if (input?.reason === 'Quit' && !this.isLeavingCall) {
          this.startRemoteQuitGrace();
        }

        VideoCallService.clearRemotePlayback();
        void this.syncRemotePresence();
        void this.verifyRemotePresenceViaApi();

        this.$emit("emit-event", {
          eventName: "enterSecondaryUID",
          data: { status: false, uid: input.user?.uid },
        });
      });
      VideoCallService.on(EVENTS.NETWORK_QUALITY, (input) => {
        this._throttledInsertNetworkQuality(input);
      });
      VideoCallService.on(EVENTS.CONNECTION_STATE, ({ curState, reason }) => {
        this.inChannel = curState === 'CONNECTED';
        if (curState === 'DISCONNECTED' && reason !== 'LEAVE' && !this.isLeavingCall) {
          void this.checkRoomSession();
        }
      });
    },

    openChat() {
      this.messageCount = 0
      this.showChatMessages = true;
      this.$refs.chat?.scrollToBottom()
    },

    closeChat() {
      this.messageCount = 0
      this.showChatMessages = false;
      this.$refs.chat?.scrollToBottom()
    },

    /**
     * Troca principal ↔ PiP ao tocar no vídeo menor.
     */
    onPipSwapClick() {
      if (this.showWaitingForRemote || this.isSwapLocked) return;
      this.isLocalMaximized = !this.isLocalMaximized;
      this.lockVideoSwap();
    },

    onLocalStageClick() {
      if (this.showWaitingForRemote) return;
      if (this.isSwapLocked || this.isLocalMaximized) return;
      this.isLocalMaximized = true;
      this.lockVideoSwap();
    },

    onRemoteStageClick() {
      if (this.showWaitingForRemote) return;
      if (this.isSwapLocked || !this.isLocalMaximized) return;
      this.isLocalMaximized = false;
      this.lockVideoSwap();
    },

    lockVideoSwap() {
      this.isSwapLocked = true;
      window.setTimeout(() => {
        this.isSwapLocked = false;
      }, 400);
    },

    toggleCameraMenu() {
      if (this.showCameraMenu) {
        this.closeCameraMenu();
        return;
      }
      this.showCameraMenu = true;
      document.addEventListener('click', this.onDocumentClickForCameraMenu, true);
    },

    closeCameraMenu() {
      this.showCameraMenu = false;
      document.removeEventListener('click', this.onDocumentClickForCameraMenu, true);
    },

    onDocumentClickForCameraMenu(event) {
      const control = this.$refs.cameraControl;
      if (control && control.contains(event.target)) return;
      this.closeCameraMenu();
    },

    async onCameraMenuToggleCamera() {
      this.closeCameraMenu();
      await this.toggleCamera();
    },

    async onCameraMenuToggleBlur() {
      this.closeCameraMenu();
      await this.toggleBlur();
    },

    /**
     * Alterna estado da câmera (ligar/desligar)
     */
    async toggleCamera() {
      // Inverter estado
      this.cameraActive = !this.cameraActive;
      this.videoOn = this.cameraActive;
      await VideoCallService.toggleVideo(this.videoOn);
      this.$emit("emit-event", {
        eventName: "toogleCamera",
        data: { status: this.cameraActive },
      });
    },

    async refreshCameraAvailability() {
      this.canSwitchCamera = await VideoCallService.canSwitchCamera();
    },

    async switchCamera() {
      if (this.isSwitchingCamera || !this.canSwitchCamera) return;
      this.isSwitchingCamera = true;
      try {
        this.cameraFacing = await VideoCallService.switchCamera();
        this.$emit("emit-event", {
          eventName: "switchCamera",
          data: { status: true, facingMode: this.cameraFacing },
        });
      } catch {
        this.$emit("emit-event", {
          eventName: "switchCamera",
          data: { status: false, facingMode: this.cameraFacing },
        });
      } finally {
        this.blurActive = VideoCallService.isBlurOn();
        this.isSwitchingCamera = false;
      }
    },

    /**
     * Alterna estado do microfone (ligar/desligar)
     */
    async toggleMicrophone() {
      // Inverter estado
      this.microphoneActive = !this.microphoneActive;
      this.audioOn = this.microphoneActive;
      await VideoCallService.toggleAudio(this.audioOn);
      this.$emit("emit-event", {
        eventName: "toogleMicrophone",
        data: { status: this.microphoneActive },
      });
    },

    /**
     * Alterna efeito de blur no fundo do vídeo
     */
    async toggleBlur() {
      // Inverter estado
      this.blurActive = !this.blurActive;
      await VideoCallService.toggleBlur(this.blurActive);
      this.$emit("emit-event", {
        eventName: "toogleBlur",
        data: { status: this.blurActive },
      });
    },

    onReceiveNewMessage(message) {
      if (message.senderUID === this.secondaryUID && !this.showChatMessages) {
        this.messageCount = this.messageCount + 1
      }
    },

    requestLeaveCall() {
      if (this.isLeavingCall) return;
      if (this.blockedByOtherDevice) {
        void this.leaveBlockedSession();
        return;
      }
      this.showLeaveConfirm = true;
      this.$nextTick(() => {
        const dialog = this.$refs.leaveConfirm;
        if (dialog && typeof dialog.focus === "function") {
          dialog.focus();
        }
      });
    },

    cancelLeaveCall() {
      if (this.isLeavingCall) return;
      this.showLeaveConfirm = false;
    },

    async confirmLeaveCall() {
      if (this.isLeavingCall) return;
      if (this.blockedByOtherDevice) {
        await this.leaveBlockedSession();
        return;
      }
      this.isLeavingCall = true;
      this.showLeaveConfirm = false;
      await this.leaveCall();
    },

    async leaveBlockedSession() {
      if (this.isLeavingCall) return;
      this.isLeavingCall = true;
      this.showLeaveConfirm = false;
      VideoCallService.cleanEvents();
      try {
        await VideoCallService.leaveRoom();
      } catch {}
      this.$emit("left-call");
      this.$emit("emit-event", {
        eventName: "leaveRoom",
        data: { status: true, code: UID_ALREADY_IN_ROOM },
      });
    },

    /**
     * Sai da videochamada e notifica o host (webview/app).
     */
    async leaveCall() {
      this.stopSessionHeartbeat();
      this.stopWaitingPoll();
      this.stopPresenceWatch();
      this.stopElapsedClock();

      this.flushCurrentWaitSegment();
      this.flushCurrentCallSegment();

      const shouldShowSummary =
        this.awaitSecondary && this.secondaryHasBeenPresent;

      if (shouldShowSummary && !this.effectiveCallEndedAt) {
        this.effectiveCallEndedAt = Date.now();
      }

      // O outro lado precisa receber o "left" antes do user-left do Agora,
      // senão exibe a tela de espera antes de encerrar.
      if (this.objAgora.nome && this.primaryUID) {
        websocketService.leaveRoom({
          roomName: this.objAgora.nome,
          localUid: this.primaryUID,
          remoteUid: this.secondaryUID ?? null
        });
      }

      try {
        if (this.objAgora?.id != null) {
          await updateStatusByUID(this.objAgora.id, 0);
        }
      } catch {}

      try {
        await VideoCallService.leaveRoom();
      } catch {}

      await this.releaseCurrentRoomSession();

      this.remotePresent = false;

      if (shouldShowSummary) {
        await this.clearPersistedTimes();
        this.$emit("left-call");
        this.$emit("emit-event", {
          eventName: "callFinished",
          data: {
            status: true,
            ...this.buildEventTimesPayload(true),
          },
        });
        return;
      }

      await this.persistAccumulatedTimes();
      this.$emit("left-call");
      this.$emit("emit-event", {
        eventName: "leaveRoom",
        data: {
          status: true,
          ...this.buildEventTimesPayload(false),
        },
      });
    },

    /**
     * Insere dados de qualidade de conexão com throttle de 2 segundos
     * Evita sobrecarga do servidor limitando as inserções
     * @param {Object} input - Dados de qualidade de rede do Agora
     */
    _throttledInsertNetworkQuality(input) {
      this.objConnection = {
        downlinkNetworkQuality: input.downlinkNetworkQuality ?? 0,
        uplinkNetworkQuality: input.uplinkNetworkQuality ?? 0,
      };

      const now = Date.now();
      const timeSinceLastInsert = now - this.lastNetworkQualityInsert;

      // Só persiste na API se passaram pelo menos 2 segundos
      if (timeSinceLastInsert >= this.networkQualityThrottleTime) {
        this.lastNetworkQualityInsert = now;

        this.$emit("emit-event", {
          eventName: "networkQuality",
          data: { ...this.objConnection },
        });

        const payload = {
          idSalas: this.objAgora.id,
          uid: this.primaryUID,
          ...input,
        };
        
        if (this.callStartedAt) {
          payload.totalDuration = Math.max(0, Math.floor(this.getTotalCallMs() / 1000));
        } else {
          payload.totalDuration = Math.max(0, Math.floor(this.accumulatedCallMs / 1000));
        }

        insertNetworkQuality(payload).catch(() => {});
      }
    },

    async _waitForAuth(timeoutMs = 8000) {
      const started = Date.now();
      while (Date.now() - started < timeoutMs) {
        if (getToken() && getClienteId()) {
          return;
        }
        await new Promise((resolve) => setTimeout(resolve, 50));
      }
    },

    /**
     * Cria ou recupera a sala na API (regra dupla de UID)
     * @async
     * @returns {Promise<object|void>}
     */
    async _insertRoomRecord() {
      try {
        await this._waitForAuth();
        const clienteId = getClienteId();
        if (!clienteId) {
          throw new Error('Cliente não autenticado');
        }

        const roomData = {
          idClientes: clienteId,
          sistema: this.system,
          primaryUID: String(this.objAgora.primaryUID),
          secondaryUID: String(this.objAgora.secondaryUID),
          primaryName: String(this.primaryName || '').trim(),
          secondaryName: String(this.secondaryName || '').trim(),
          ativo: 1,
          sessionId: this.sessionId,
        };

        const response = await insertRoom(roomData);

        return response;
      } catch (error) {
        if (isUidAlreadyInRoomError(error)) {
          throw error;
        }
      }
    },
  },
};
</script>
